import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@repo/ui/components/Base/Input";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { DatePicker } from "@repo/ui/components/Base/DatePicker";
import { ComboInput } from "@repo/ui/components/Base/ComboInput";

// ===== helpers =====
const getYears = (start = 2020, extraYears = 5, descending = false) => {
  const end = new Date().getFullYear() + extraYears;
  if (start > end) return [];
  const years = Array.from({ length: end - start + 1 }, (_, i) => start + i);
  return descending ? years.reverse() : years;
};

const MONTHS_MM = [
  "01",
  "02",
  "03",
  "04",
  "05",
  "06",
  "07",
  "08",
  "09",
  "10",
  "11",
  "12",
  "Q1",
  "Q2",
  "Q3",
  "Q4",
] as const;
type MonthMM = (typeof MONTHS_MM)[number];

const pad2 = (n: number | string) => String(n).padStart(2, "0");
const normMonth = (m: string): MonthMM => pad2(m) as MonthMM;

type NameParts = {
  title: string;
  year?: number;
  month?: MonthMM; // "01".."12" or "Q1".."Q4"
  day?: string;
};

// Parse "Title", "Title YYYY[-MM[-DD]]", "Title YYYY-Q[1-4]" -> parts
const parseNameToParts = (name: string): NameParts => {
  // 1=title, 2=year, 3=mm, 4=dd, 5=quarter num
  const m = name?.match(
    /^(.+?)\s(\d{4})(?:-(?:(\d{2})(?:-(\d{2}))?|Q([1-4])))?$/
  );
  if (!m) return { title: name?.trim() ?? "" };
  const [, title, y, mm, dd, q] = m;
  const month = q ? (`Q${q}` as MonthMM) : (mm as MonthMM | undefined);
  return {
    title: title.trim(),
    year: Number(y),
    month: month ?? undefined,
    day: dd,
  };
};

// Build "Title", "Title YYYY[-MM[-DD]]" or "Title YYYY-Q[1-4]"
const buildNameFromParts = ({ title, year, month, day }: NameParts) => {
  const t = (title ?? "").trim();
  if (!year) return t;

  let base = `${t} ${year}`;
  if (month) {
    base += `-${month}`;
    if (day && !String(month).startsWith("Q")) base += `-${day}`;
  }
  return base;
};

// ===== Component =====
export interface TitleComposerProps {
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  isSubmitting?: boolean;
  titleLabel?: string;
  monthNoneLabel?: string;
  yearStart?: number;
  yearExtra?: number;
  yearNoneLabel?: string;
  isNew?: boolean;
}

export const TitleComposer: React.FC<TitleComposerProps> = ({
  value,
  onChange,
  onBlur,
  disabled,
  isSubmitting,
  titleLabel = "*Item Title",
  monthNoneLabel = "None",
  yearStart = 2020,
  yearExtra = 5,
  yearNoneLabel = "None",
  isNew = false,
}) => {
  const years = useMemo(
    () => getYears(yearStart, yearExtra, false),
    [yearStart, yearExtra]
  );

  const [titlePart, setTitlePart] = useState<string>("");
  const [yearPart, setYearPart] = useState<string>("");
  const [monthPart, setMonthPart] = useState<MonthMM | "none">("none");
  const [dayPart, setDayPart] = useState<string | undefined>(undefined);

  const [yearError, setYearError] = useState<string | null>(null);

  const hasInteracted = useRef(false);

  useEffect(() => {
    if (hasInteracted.current) return;
    const p = parseNameToParts(value ?? "");
    setTitlePart(p.title ?? "");
    setYearPart(
      p.year != null
        ? String(p.year)
        : isNew
          ? new Date().getFullYear().toString()
          : ""
    );
    setMonthPart((p.month as MonthMM | undefined) ?? "none");
    setDayPart(p.day);
  }, [isNew, value]);

  const emitIfChanged = (patch: Partial<NameParts>) => {
    // year
    const resolvedYear =
      "year" in patch
        ? patch.year
        : yearPart === "" || yearPart === undefined
          ? undefined
          : Number(yearPart);

    // month
    const resolvedMonth =
      "month" in patch
        ? patch.month
        : monthPart === "none"
          ? undefined
          : monthPart;

    // day
    let resolvedDay = "day" in patch ? patch.day : dayPart;
    if (
      !resolvedYear ||
      !resolvedMonth ||
      String(resolvedMonth).startsWith("Q")
    ) {
      resolvedDay = undefined;
    }

    const composed: NameParts = {
      title: "title" in patch ? patch.title ?? "" : titlePart,
      year: resolvedYear,
      month: resolvedMonth,
      day: resolvedDay,
    };

    const next = buildNameFromParts(composed);

    if (next !== value) onChange(next);
  };

  const dateValue: Date | undefined = useMemo(() => {
    if (
      yearPart !== "none" &&
      dayPart &&
      monthPart !== "none" &&
      /^\d{2}$/.test(String(monthPart))
    ) {
      const d = new Date(
        Number(yearPart),
        Number(monthPart) - 1,
        Number(dayPart)
      );
      return isNaN(d.getTime()) ? undefined : d;
    }
    return undefined;
  }, [yearPart, monthPart, dayPart]);

  return (
    <div className="flex flex-row gap-4">
      <div className="w-full">
        <Input
          disabled={disabled || isSubmitting}
          label={titleLabel}
          id="title-composer-title"
          name="title-composer-title"
          value={titlePart}
          onChange={(e) => {
            hasInteracted.current = true;
            const v = e.target.value;
            setTitlePart(v);
            emitIfChanged({ title: v });
          }}
          onBlur={onBlur}
        />
      </div>

      <ComboInput
        id="title-composer-year"
        label="Year"
        className="w-28 shrink-0"
        value={yearPart}
        onChange={(e) => {
          hasInteracted.current = true;
          const val = e.target.value;
          const isValid = val === "" || /^\d{4}$/.test(val);
          setYearError(isValid ? null : "Invalid");
          if (val === "") {
            setYearPart("");
            setMonthPart("none");
            setDayPart(undefined);
            emitIfChanged({
              year: undefined,
              month: undefined,
              day: undefined,
            });
          } else {
            setYearPart(val);
            emitIfChanged({ year: Number(val) });
          }
        }}
        disabled={disabled || isSubmitting}
        options={[
          { value: "", label: yearNoneLabel },
          ...years.map((y) => ({ value: String(y), label: String(y) })),
        ]}
        error={!!yearError}
        errorMessage={yearError ?? undefined}
      />

      <SelectInput
        label="Month"
        containerStyle="w-20 shrink-0"
        value={monthPart}
        onValueChange={(val) => {
          hasInteracted.current = true;
          const raw = val as MonthMM | "none";
          const next =
            raw === "none"
              ? "none"
              : raw.startsWith("Q")
                ? (raw as MonthMM)
                : normMonth(raw);

          setMonthPart(next);
          setDayPart(undefined);

          emitIfChanged({
            month:
              yearPart === "none"
                ? undefined
                : next === "none"
                  ? undefined
                  : next,
            day: undefined,
          });
        }}
        disabled={disabled || isSubmitting || yearPart === ""}
        options={[
          <SelectItem key="none" value="none">
            {monthNoneLabel}
          </SelectItem>,
          ...MONTHS_MM.map((m) => (
            <SelectItem key={m} value={m}>
              {m}
            </SelectItem>
          )),
        ]}
      />

      <DatePicker
        id="title-composer-date"
        label="Date"
        className="w-20"
        placeholder="None"
        dateFormat="dd"
        showIcon={false}
        date={dateValue}
        onDateChange={(date) => {
          hasInteracted.current = true;
          if (date) {
            const y = date.getFullYear();
            const m = pad2(date.getMonth() + 1) as MonthMM;
            const d = pad2(date.getDate());
            setYearPart(String(y));
            setMonthPart(m);
            setDayPart(d);
            emitIfChanged({ year: y, month: m, day: d }); // Title YYYY-MM-DD
          } else {
            setDayPart(undefined);
            emitIfChanged({ day: undefined }); // Title YYYY-MM or Title YYYY
          }
        }}
        calendarProps={{
          defaultMonth: dateValue
            ? dateValue
            : /^\d{4}$/.test(yearPart ?? "") && /^\d{2}$/.test(monthPart ?? "")
              ? new Date(+yearPart!, +monthPart - 1, 1)
              : new Date(),
        }}
        disabled={
          disabled ||
          isSubmitting ||
          // yearPart === "" ||
          // monthPart === "none" ||
          String(monthPart).startsWith("Q")
        }
      />
    </div>
  );
};
